import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
  Dialog, DialogContent, DialogActions,
  Alert, Box, Button, Chip, IconButton, InputAdornment, MenuItem, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PersonIcon from '@mui/icons-material/Person';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import * as actions from '../../redux/actions';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';

// คอลัมน์ปลายทาง (ตรงกับ TARGET_FIELDS ฝั่ง backend) — revenue.* = เพิ่มรายได้, expenses.* = เพิ่มยอดหัก
export const TARGET_FIELD_LABEL = {
  'revenue.salary': 'ค่าแรง',
  'revenue.overtime': 'OT',
  'revenue.allowances': 'เบี้ยเลี้ยง',
  'revenue.field_allowance': 'ค่าออกสนาม',
  'revenue.bonus': 'เงินได้อื่นๆ',
  'expenses.sso': 'ประกันสังคม',
  'expenses.tax': 'หักภาษี',
  'expenses.crl': 'หัก CRL',
  'expenses.other': 'หักอื่นๆ',
};
const TARGET_OPTIONS = Object.keys(TARGET_FIELD_LABEL);
const isDeduct = (tf) => String(tf || '').startsWith('expenses.');
const DEFAULT_TARGET = { deduct: 'expenses.other', add: 'revenue.bonus' };

const fmtBaht = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPeriod = (p) => {
  if (!p?.dateStart) return '—';
  const s = dayjs(p.dateStart);
  const e = dayjs(p.dateEnd);
  return s.month() === e.month()
    ? `${s.format('D')}–${e.format('D MMM YY')}`
    : `${s.format('D MMM')}–${e.format('D MMM YY')}`;
};
// งวดถัดไป — กฎเดียวกับ nextPeriod() ฝั่ง backend (payrollCarryover.service.js) ใช้โชว์ให้ HR เห็นก่อนกดเพิ่ม
const nextPeriodOf = (p, roleType) => {
  if (!p?.dateStart) return null;
  const s = dayjs(p.dateStart);
  if (roleType === 'DAILY') {
    if (s.date() <= 15) return { dateStart: s.date(16), dateEnd: s.endOf('month') };
    const nm = s.add(1, 'month');
    return { dateStart: nm.startOf('month'), dateEnd: nm.date(15) };
  }
  const nm = s.add(1, 'month');
  return { dateStart: nm.startOf('month'), dateEnd: nm.endOf('month') };
};
const samePeriod = (a, b) => !!a?.dateStart && !!b?.dateStart
  && Math.abs(dayjs(a.dateStart).valueOf() - dayjs(b.dateStart).valueOf()) < 60000
  && Math.abs(dayjs(a.dateEnd).valueOf() - dayjs(b.dateEnd).valueOf()) < 60000;
const signedAmount = (r) => (isDeduct(r.target_field) ? -1 : 1) * (Number(r.amount) || 0);

const STATUS_META = {
  pending: { label: 'รอใช้', color: '#b45309', bg: '#fef3c7' },
  applied: { label: 'ใช้แล้ว', color: '#15803d', bg: '#dcfce7' },
  cancelled: { label: 'ยกเลิก', color: '#64748b', bg: '#f1f5f9' },
};
const FILTERS = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'pending', label: 'รอใช้' },
  { key: 'applied', label: 'ใช้แล้ว' },
  { key: 'cancelled', label: 'ยกเลิก' },
];

// การ์ดสรุปตัวเลข 1 ช่อง
function StatCard({ icon, label, value, hint, color }) {
  return (
    <Box
      sx={{
        flex: '1 1 160px',
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', mb: 0.5 }}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color, lineHeight: 1.3 }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

const signedText = (n) => `${n >= 0 ? '+' : '−'}${fmtBaht(Math.abs(n))}`;
const signedColor = (n) => {
  if (n > 0) return '#15803d';
  if (n < 0) return '#b91c1c';
  return 'text.primary';
};

export default function CarryoverDialog({
  open, onClose, employee, roleType, currentPeriod, periodLabel, readOnly = false, onChanged,
}) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState('deduct');
  const [amount, setAmount] = useState('');
  const [targetField, setTargetField] = useState(DEFAULT_TARGET.deduct);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [filter, setFilter] = useState('all');

  const empId = employee?.employee_id;

  const load = useCallback(async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const data = await dispatch(actions.payrollCarryoverByEmployee(empId));
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, empId]);

  useEffect(() => {
    if (open && empId) {
      load();
      setDirection('deduct');
      setAmount('');
      setNote('');
      setTargetField(DEFAULT_TARGET.deduct);
      setEditId(null);
      setEditVal('');
      setFilter('all');
    }
  }, [open, empId, load]);

  // ยอดที่จะเข้างวดนี้ (pending + target_period ตรงกับงวดที่เปิดอยู่)
  const incoming = useMemo(
    () => rows.filter((r) => r.status === 'pending' && samePeriod(r.target_period, currentPeriod)),
    [rows, currentPeriod],
  );
  const incomingNet = useMemo(() => incoming.reduce((acc, r) => acc + signedAmount(r), 0), [incoming]);

  // ยอดที่งวดนี้ยกไปงวดหน้าอัตโนมัติ (ติดลบตอนบันทึกเงินเดือน)
  const outgoingTotal = useMemo(
    () => rows
      .filter((r) => r.source === 'auto' && r.status !== 'cancelled' && samePeriod(r.source_period, currentPeriod))
      .reduce((acc, r) => acc + (Number(r.amount) || 0), 0),
    [rows, currentPeriod],
  );

  const pendingNet = useMemo(
    () => rows.filter((r) => r.status === 'pending').reduce((acc, r) => acc + signedAmount(r), 0),
    [rows],
  );

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, applied: 0, cancelled: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [rows]);

  const visibleRows = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const nextPeriod = nextPeriodOf(currentPeriod, roleType);
  const amtNum = Number(amount);
  const amountValid = amount !== '' && Number.isFinite(amtNum) && amtNum > 0;

  const changeDirection = (_e, v) => {
    if (!v) return;
    setDirection(v);
    setTargetField(DEFAULT_TARGET[v]);
  };

  const handleAdd = async () => {
    if (!amountValid) {
      showAlert('กรอกจำนวนเงินให้ถูกต้อง (มากกว่า 0)');
      return;
    }
    setSaving(true);
    try {
      await dispatch(actions.payrollCarryoverCreate({
        employee_id: empId,
        role_type: roleType,
        amount: amtNum,
        target_field: targetField,
        current_period: { dateStart: currentPeriod.dateStart, dateEnd: currentPeriod.dateEnd },
        note: note.trim() || null,
      }));
      setAmount('');
      setNote('');
      await load();
      onChanged?.();
    } catch (e) {
      showAlert(e?.response?.data?.message || 'เพิ่มรายการไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row._id || row.id);
    setEditVal(String(row.amount ?? ''));
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditVal('');
  };
  const saveEdit = async (row) => {
    const amt = Number(editVal);
    if (!Number.isFinite(amt) || amt < 0) {
      showAlert('จำนวนเงินไม่ถูกต้อง');
      return;
    }
    try {
      await dispatch(actions.payrollCarryoverUpdate(row._id || row.id, { amount: amt }));
      cancelEdit();
      await load();
      onChanged?.();
    } catch (e) {
      showAlert(e?.response?.data?.message || 'แก้ไขไม่สำเร็จ');
    }
  };

  const handleCancel = async (row) => {
    if (!(await confirmAsync('ยกเลิกรายการยกยอดนี้?'))) return;
    try {
      await dispatch(actions.payrollCarryoverCancel(row._id || row.id));
      await load();
      onChanged?.();
    } catch (e) {
      showAlert(e?.response?.data?.message || 'ยกเลิกไม่สำเร็จ');
    }
  };

  const handleDelete = async (row) => {
    if (!(await confirmAsync('ลบรายการยกยอดนี้ถาวร?'))) return;
    try {
      await dispatch(actions.payrollCarryoverDelete(row._id || row.id));
      await load();
      onChanged?.();
    } catch (e) {
      showAlert(e?.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  const renderRow = (r) => {
    const id = r._id || r.id;
    const deduct = isDeduct(r.target_field);
    const canEdit = !readOnly && r.status === 'pending';
    const isEditing = editId === id;
    const meta = STATUS_META[r.status] || STATUS_META.cancelled;
    const muted = r.status === 'cancelled';
    const isIncomingNow = r.status === 'pending' && samePeriod(r.target_period, currentPeriod);
    return (
      <Box
        key={id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          opacity: muted ? 0.6 : 1,
          bgcolor: isIncomingNow ? 'rgba(59,130,246,0.05)' : 'transparent',
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        {/* จำนวนเงิน + คอลัมน์ปลายทาง */}
        <Box sx={{ minWidth: 150 }}>
          {isEditing ? (
            <TextField
              type="number"
              size="small"
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(r); if (e.key === 'Escape') cancelEdit(); }}
              autoFocus
              sx={{ width: 140 }}
              InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          ) : (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                color: deduct ? '#b91c1c' : '#15803d',
                textDecoration: muted ? 'line-through' : 'none',
              }}
            >
              {deduct ? '−' : '+'}{fmtBaht(r.amount)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {deduct ? 'หักใน' : 'เพิ่มใน'} {TARGET_FIELD_LABEL[r.target_field] || r.target_field}
          </Typography>
        </Box>

        {/* รายละเอียด */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ลงงวด {fmtPeriod(r.target_period)}
            </Typography>
            {isIncomingNow && (
              <Chip size="small" label="งวดนี้" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            {r.source === 'auto'
              ? <AutorenewIcon sx={{ fontSize: 13 }} />
              : <PersonIcon sx={{ fontSize: 13 }} />}
            <Typography variant="caption">
              {r.source === 'auto'
                ? `อัตโนมัติ (ติดลบจากงวด ${fmtPeriod(r.source_period)})`
                : 'HR เพิ่มเอง'}
              {' · '}{dayjs(r.createdAt).format('DD/MM/YY HH:mm')}
            </Typography>
          </Box>
          {r.note && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', mt: 0.25 }} noWrap title={r.note}>
              “{r.note}”
            </Typography>
          )}
        </Box>

        {/* สถานะ */}
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 10,
            fontSize: '0.72rem',
            fontWeight: 700,
            color: meta.color,
            bgcolor: meta.bg,
            whiteSpace: 'nowrap',
          }}
        >
          {meta.label}
        </Box>

        {/* จัดการ */}
        <Box sx={{ display: 'flex', minWidth: 96, justifyContent: 'flex-end' }}>
          {canEdit && isEditing && (
            <>
              <Tooltip title="บันทึก">
                <IconButton size="small" color="primary" onClick={() => saveEdit(r)}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="ยกเลิกการแก้">
                <IconButton size="small" onClick={cancelEdit}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canEdit && !isEditing && (
            <>
              <Tooltip title="แก้จำนวน">
                <IconButton size="small" onClick={() => startEdit(r)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="ยกเลิกรายการ">
                <IconButton size="small" onClick={() => handleCancel(r)}>
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {r.source === 'manual' && (
                <Tooltip title="ลบถาวร">
                  <IconButton size="small" color="error" onClick={() => handleDelete(r)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      {/* หัว */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, position: 'relative' }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>ยอดยกไปงวดหน้า</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{employee?.name || '—'}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" variant="outlined" label={`งวดที่เปิดอยู่: ${periodLabel || ''} · ${fmtPeriod(currentPeriod)}`} />
          {roleType && (
            <Chip size="small" variant="outlined" label={roleType === 'DAILY' ? 'รายวัน' : 'รายเดือน'} />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
        {/* สรุป */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <StatCard
            icon={<CallReceivedIcon sx={{ fontSize: 16 }} />}
            label="ยกมาเข้างวดนี้"
            value={incoming.length ? signedText(incomingNet) : '—'}
            hint={incoming.length ? `${incoming.length} รายการ` : 'ไม่มีรายการ'}
            color={signedColor(incomingNet)}
          />
          <StatCard
            icon={<CallMadeIcon sx={{ fontSize: 16 }} />}
            label="งวดนี้ยกไปงวดหน้า"
            value={outgoingTotal > 0 ? `−${fmtBaht(outgoingTotal)}` : '—'}
            hint={outgoingTotal > 0 ? 'ติดลบตอนบันทึกเงินเดือน' : 'ไม่มีรายการ'}
            color={outgoingTotal > 0 ? '#b45309' : 'text.primary'}
          />
          <StatCard
            icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
            label="รอใช้ทั้งหมด"
            value={counts.pending ? signedText(pendingNet) : '—'}
            hint={counts.pending ? `${counts.pending} รายการ (ทุกงวด)` : 'ไม่มีรายการ'}
            color={signedColor(pendingNet)}
          />
        </Box>

        {/* ฟอร์มเพิ่ม (HR เพิ่มเอง) */}
        {!readOnly && (
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>เพิ่มรายการยกยอด</Typography>
              <ToggleButtonGroup size="small" exclusive value={direction} onChange={changeDirection}>
                <ToggleButton value="deduct" sx={{ px: 1.5, '&.Mui-selected': { color: '#b91c1c', bgcolor: '#fee2e2' } }}>
                  − หักเงิน
                </ToggleButton>
                <ToggleButton value="add" sx={{ px: 1.5, '&.Mui-selected': { color: '#15803d', bgcolor: '#dcfce7' } }}>
                  + เพิ่มรายได้
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                label="จำนวนเงิน"
                type="number"
                size="small"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && amountValid && !saving) handleAdd(); }}
                sx={{ width: 160 }}
                InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                select
                label={direction === 'deduct' ? 'หักในคอลัมน์' : 'เพิ่มในคอลัมน์'}
                size="small"
                value={targetField}
                onChange={(e) => setTargetField(e.target.value)}
                sx={{ width: 170 }}
              >
                {TARGET_OPTIONS
                  .filter((tf) => (direction === 'deduct' ? isDeduct(tf) : !isDeduct(tf)))
                  .map((tf) => <MenuItem key={tf} value={tf}>{TARGET_FIELD_LABEL[tf]}</MenuItem>)}
              </TextField>
              <TextField
                label="หมายเหตุ (ไม่บังคับ)"
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                disabled={saving || !amountValid}
                sx={{ height: 40 }}
              >
                เพิ่ม
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              จะลงในงวดถัดไป: <b>{fmtPeriod(nextPeriod)}</b>
              {amountValid && (
                <>
                  {' · '}
                  <Box component="span" sx={{ color: direction === 'deduct' ? '#b91c1c' : '#15803d', fontWeight: 700 }}>
                    {direction === 'deduct' ? '−' : '+'}{fmtBaht(amtNum)}
                  </Box>
                  {' '}ใน {TARGET_FIELD_LABEL[targetField]}
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* รายการทั้งหมด */}
        <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>ประวัติทั้งหมด</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {FILTERS.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={`${f.label} ${counts[f.key]}`}
                  color={filter === f.key ? 'primary' : 'default'}
                  variant={filter === f.key ? 'filled' : 'outlined'}
                  onClick={() => setFilter(f.key)}
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', overflowX: 'auto' }}>
            <Box sx={{ minWidth: 560 }}>
              {loading && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>กำลังโหลด…</Typography>
              )}
              {!loading && visibleRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                  {rows.length === 0 ? 'ยังไม่มีรายการยกยอดของพนักงานคนนี้' : 'ไม่มีรายการในสถานะนี้'}
                </Typography>
              )}
              {!loading && visibleRows.map(renderRow)}
            </Box>
          </Box>
        </Box>

        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mt: 2, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
          บนหน้าจอ ยอดยกมาแสดงแยกจากตัวเลขในคอลัมน์ แต่ตอนบันทึกเงินเดือน/พิมพ์ จะถูกรวมเข้าคอลัมน์ปลายทางจริง
          จึงมีผลกับยอดสุทธิ ยอดต่อคน และยอดนำส่งภาษี/ประกันสังคมของงวดปลายทาง — แก้/ยกเลิกได้เฉพาะรายการที่ยัง &quot;รอใช้&quot;
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
